import { combineEpics, ofType } from 'redux-observable';
import I18n from 'i18n-js';
import uuidV4 from 'uuid/v4';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _isEmpty from 'lodash/isEmpty';
import _cloneDeep from 'lodash/cloneDeep';
import _every from 'lodash/every';
import { mergeMap, catchError, debounceTime, filter } from 'rxjs/operators';
import { forkJoin, of, EMPTY } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import NewBulkUploadDuck from 'ducks/new-bulk-upload-duck';
import NewBookingDuck from 'ducks/new-booking-duck';
import {
  fetchOffersEndpoint,
  fetchOffersByPromoCodeEndpoint,
} from 'actions/booking-endpoints';
import geoEndpoints from 'actions/geo-endpoints';
import {
  deliveryIDPrefixes,
  genDeliveryID,
} from 'constants/anytime-booking-constants';
import { buildResolvePOIsPayload } from 'helper/address-search';
import {
  bulkUploadApplicableOfferConfigSelector,
  bulkUploadDeliveriesSelector,
  bulkUploadDeliveryIDsSelector,
  bulkUploadGroupingClusterMappingsSelector,
  bulkUploadGroupingTrialDeliveriesSelector,
  bulkUploadOfferConfigSelector,
  bulkUploadRawDataSelector,
} from 'selectors/new-bulk-upload-selectors';
import {
  enterpriseSelector,
  isPaxSelector,
  itemCategoriesSelector,
  selectedCitySelector,
  userCompanyIDSelector,
  featureFlagsSelector,
} from 'selectors/app-selector';
import fetchObservableUtil from 'common/utils/fetch-observable';
import { buildHeadersV2, buildAjaxCredentials } from 'helper/ajax-helper';
import {
  getClusteringEnpoint,
  getMetaDataEnpoint,
} from 'actions/bulk-upload-endpoints';
import { SENDER_POI, DROP_OFF_POI } from 'constants/new-bulk-upload-const';
import {
  addMoreStepToDeliveryData,
  getKeyColumnFormat,
  rowToDeliveryData,
} from 'components/v2/anytime-booking/common/new-bulk-upload-common';
import { pushDeleteDeliveryNotification } from 'components/v2/anytime-booking/notifications/notifications';
import {
  bookingConfigurationSelector,
  deliveriesFromPropsSelector,
  deliverySelector,
} from 'selectors/new-booking-selectors';
import {
  ajaxFetchDeliveriesQuote,
  applyRouteOptimization,
  isRewardOffer,
  mergeDeliveriesIntoMSD,
  parseItemInfo,
} from 'helper/anytime-booking';
import { injectUuidToPOI } from 'helper/geo-helper';
// eslint-disable-next-line import/no-cycle
import store from 'store';
import { pushNotificationAction } from 'actions/app-actions';
import { NOTIFICATION_TYPE } from 'components/v2/notification/notification';
import { buildCredentials } from '../../helper/session-helper';

const buildBulkUploadFetchHeader = state$ => {
  const enterprise = enterpriseSelector(state$.value);
  const isPax = isPaxSelector(state$.value);
  return buildHeadersV2({
    isPax,
    companyID: _get(enterprise, 'companyID'),
    includeInternalHeaders: true,
  });
};

const deleteDeliveryEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBulkUploadDuck.types.DELETE_DELIVERY),
    mergeMap(action => {
      const { deliveryID } = action;
      const delivery = deliverySelector(state$.value, { deliveryID });
      const revertFunc = () => {
        store.dispatch(NewBulkUploadDuck.creators.restoreDelivery(deliveryID));
      };
      return of(pushDeleteDeliveryNotification(delivery, revertFunc));
    }),
  );

const createNewDeliveriesEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBulkUploadDuck.types.CREATE_NEW_DELIVERIES),
    mergeMap(() => {
      const featureFlags = featureFlagsSelector(state$.value);
      const rawData = bulkUploadRawDataSelector(state$.value);
      const itemCategories = itemCategoriesSelector(state$.value);
      const bookingConfiguration = bookingConfigurationSelector(state$.value);
      const categoryReverseMap = Object.fromEntries(
        itemCategories.map(({ id, name }) => [name, id]),
      );
      const batchToDeliveryData = {};
      const newDeliveryData = [];
      const keyColumnFormat = getKeyColumnFormat(featureFlags);
      rawData.forEach(row => {
        const { batch, sequenceNumber } = row;
        if (!batch || sequenceNumber === 1) {
          // process single booking or first step in batch
          const deliveryData = rowToDeliveryData(
            row,
            categoryReverseMap,
            keyColumnFormat,
            featureFlags,
          );
          if (batch) {
            batchToDeliveryData[batch] = deliveryData;
          }
          newDeliveryData.push(deliveryData);
        } else {
          // process 2nd+ steps in batch
          const deliveryData = batchToDeliveryData[batch];
          addMoreStepToDeliveryData(
            row,
            deliveryData,
            categoryReverseMap,
            keyColumnFormat,
            featureFlags,
          );
        }
      });

      return of(
        NewBookingDuck.creators.bulkCreateNewDeliveries(
          newDeliveryData,
          bookingConfiguration,
        ),
        NewBulkUploadDuck.creators.linkNewDeliveries(newDeliveryData),
        NewBulkUploadDuck.creators.setOfferConfig(null),
        NewBulkUploadDuck.creators.setApplicableOfferConfig(null),
      );
    }),
  );

const fetchMetaDataEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBulkUploadDuck.types.FETCH_METADATA),
    mergeMap(() => {
      const cityID = _get(selectedCitySelector(state$.value), 'cityID');

      return fetchObservableUtil(getMetaDataEnpoint({ cityID }), {
        method: 'GET',
        headers: buildBulkUploadFetchHeader(state$),
        credentials: buildCredentials(),
      }).pipe(catchError(error => of({ error })));
    }),
    mergeMap(data =>
      of(
        NewBulkUploadDuck.creators.updateMetaData({
          metaData: data,
        }),
      ),
    ),
  );

const resolveKey = (isPickUp, keyword, adminArea) =>
  `${isPickUp ? 'p' : 'd'}-${_get(adminArea, 'path')}-${keyword}`;

const fetchPOIsForRawDataEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBulkUploadDuck.types.FETCH_POIS_FOR_RAW_DATA),
    mergeMap(() => {
      const city = selectedCitySelector(state$.value);
      const rawData = bulkUploadRawDataSelector(state$.value);

      const keyIndex = {};
      const resolvingRequests = [];
      rawData.forEach(row => {
        const {
          batch,
          sequenceNumber,
          senderAddress,
          senderAdminArea,
          receiverAddress,
          receiverAdminArea,
        } = row;
        if (senderAddress && (!batch || sequenceNumber === 1)) {
          const key = resolveKey(true, senderAddress, senderAdminArea);
          if (keyIndex[key] === undefined) {
            keyIndex[key] = resolvingRequests.length;
            resolvingRequests.push({
              key,
              pickupOrDropoff: 'pickUp',
              keyword: senderAddress,
              adminAreaPath: _get(senderAdminArea, 'path'),
            });
          }
        }
        if (receiverAddress) {
          const key = resolveKey(false, receiverAddress, receiverAdminArea);
          if (keyIndex[key] === undefined) {
            keyIndex[key] = resolvingRequests.length;
            resolvingRequests.push({
              key,
              pickupOrDropoff: 'dropOff',
              keyword: receiverAddress,
              adminAreaPath: _get(receiverAdminArea, 'path'),
            });
          }
        }
      });
      const payload = buildResolvePOIsPayload(resolvingRequests, city);

      return forkJoin(
        of(rawData),
        of(keyIndex),
        ajax({
          url: geoEndpoints.v3ResolvePOIsEndpoint(),
          method: 'POST',
          headers: buildBulkUploadFetchHeader(state$),
          withCredentials: buildAjaxCredentials(),
          body: JSON.stringify(payload),
          timeout: 15000,
        }).pipe(catchError(error => of({ error }))),
      );
    }),
    mergeMap(([rawData, keyIndex, resolveResults]) => {
      const updatedPOIs = {};
      const updatePOIFromResolveResult = (key, id, fieldName) => {
        const index = keyIndex[key];
        if (index !== undefined) {
          const poi = _get(resolveResults, [
            'response',
            'response',
            index,
            'result',
            0,
          ]);
          const searchUUID = _get(resolveResults, [
            'response',
            'response',
            index,
            'uuid',
          ]);
          injectUuidToPOI(poi, searchUUID);
          if (poi) {
            _set(updatedPOIs, [id, fieldName], poi);
          }
        }
      };

      rawData.forEach(row => {
        const {
          id,
          batch,
          sequenceNumber,
          senderAddress,
          senderAdminArea,
          receiverAddress,
          receiverAdminArea,
        } = row;
        if (senderAddress && (!batch || sequenceNumber === 1)) {
          const key = resolveKey(true, senderAddress, senderAdminArea);
          updatePOIFromResolveResult(key, id, SENDER_POI);
        }
        if (receiverAddress) {
          const key = resolveKey(false, receiverAddress, receiverAdminArea);
          updatePOIFromResolveResult(key, id, DROP_OFF_POI);
        }
      });
      return of(NewBulkUploadDuck.creators.updatePOIsForRawData(updatedPOIs));
    }),
  );

const fetchPromoEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBulkUploadDuck.types.FETCH_OFFER_LIST),
    mergeMap(() => {
      const cityLatlng = _get(
        selectedCitySelector(state$.value),
        'coordinates',
      );
      if (!cityLatlng) {
        return EMPTY;
      }
      const companyID = userCompanyIDSelector(state$.value);
      const isPax = isPaxSelector(state$.value);

      return fetchObservableUtil(fetchOffersEndpoint(), {
        method: 'POST',
        headers: buildHeadersV2({
          isPax,
          companyID,
          includeInternalHeaders: true,
        }),
        credentials: buildCredentials(isPax),
        body: JSON.stringify(cityLatlng),
      });
    }),
    mergeMap(({ offers = [] }) =>
      of(NewBulkUploadDuck.creators.updateOfferList(offers)),
    ),
  );

const inputPromoCodeEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBulkUploadDuck.types.INPUT_PROMO_CODE),
    debounceTime(500),
    mergeMap(action => {
      const { promoCode } = action;
      const cityLatlng = _get(
        selectedCitySelector(state$.value),
        'coordinates',
      );
      if (!cityLatlng) {
        return EMPTY;
      }

      const companyID = userCompanyIDSelector(state$.value);
      const isPax = isPaxSelector(state$.value);
      return fetchObservableUtil(
        fetchOffersByPromoCodeEndpoint({
          ...cityLatlng,
          promoCode,
        }),
        {
          method: 'GET',
          headers: buildHeadersV2({
            isPax,
            companyID,
            includeInternalHeaders: true,
          }),
          credentials: buildCredentials(isPax),
        },
        { rethrow: true },
      ).pipe(
        mergeMap(({ offers = [] }) =>
          of(NewBulkUploadDuck.creators.updateOfferList(offers)),
        ),
        catchError(err => EMPTY),
      );
    }),
  );

const trySetOfferConfigBulkNullEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBulkUploadDuck.types.TRY_SET_OFFER_CONFIG),
    filter(action => {
      const { offer } = action;
      return !offer;
    }),
    mergeMap(() => {
      const deliveryIDs = bulkUploadDeliveryIDsSelector(state$.value);
      const obs = [];
      obs.push(NewBulkUploadDuck.creators.setOfferConfig(null));
      obs.push(NewBulkUploadDuck.creators.setApplicableOfferConfig(null));
      deliveryIDs.forEach(deliveryID => {
        obs.push(NewBookingDuck.creators.setOfferConfig(deliveryID, null));
      });
      return of(...obs);
    }),
  );

const trySetOfferConfigBulkEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBulkUploadDuck.types.TRY_SET_OFFER_CONFIG),
    filter(action => {
      const { offer } = action;
      const offerConfig = bulkUploadOfferConfigSelector(state$.value);

      return (
        !!offer &&
        _get(offer, 'offerID') &&
        _get(offerConfig, 'offerID') !== _get(offer, 'offerID')
      );
    }),
    mergeMap(action => {
      const { offer } = action;
      const clonedDeliveries = _cloneDeep(
        bulkUploadDeliveriesSelector(state$.value),
      );
      clonedDeliveries.forEach(delivery => {
        _set(delivery, ['detail', 'configuration', 'offerConfig'], offer);
      });
      const deliveryIDs = clonedDeliveries.map(delivery => delivery.id);

      return forkJoin(
        of(offer),
        of(deliveryIDs),
        ajaxFetchDeliveriesQuote(clonedDeliveries, state$.value),
      );
    }),
    mergeMap(([offer, deliveryIDs, resp]) => {
      const { response, error } = resp;
      const payloads = _get(response, 'quotes', []);

      if (error || payloads.length !== deliveryIDs.length) {
        // Silently catch for now;
        return EMPTY;
      }

      const obs = [];
      const updateQuoteAndConfigs = {};
      deliveryIDs.forEach((deliveryID, i) => {
        const payload = payloads[i];
        if (!_get(payload, ['quote', 'discountEligibilityError'], null)) {
          updateQuoteAndConfigs[deliveryID] = {
            quote: undefined,
            fareDetailMatrix: undefined,
            config: undefined,
            ...payload,
          };
        }
      });

      const bestDiscounts = getBestDiscountWithOffers(
        updateQuoteAndConfigs,
        offer,
      );
      if (deliveryIDs.length === Object.keys(bestDiscounts).length) {
        // If can apply to all deliveries
        obs.push(
          ...applyBestDiscountToOffer(deliveryIDs, offer, bestDiscounts),
        );
      } else {
        // If there is some delivery cannot apply offer
        obs.push(
          NewBulkUploadDuck.creators.setApplicableOfferConfig({
            offerConfig: offer,
            discountConfig: bestDiscounts,
          }),
        );
      }

      return of(...obs);
    }),
  );

const applyApplicableOfferConfigEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBulkUploadDuck.types.APPLY_APPLICABLE_OFFER_CONFIG),
    mergeMap(action => {
      const applicableOfferConfig = bulkUploadApplicableOfferConfigSelector(
        state$.value,
      );
      const discountConfig = _get(applicableOfferConfig, 'discountConfig');
      const offer = _get(applicableOfferConfig, 'offerConfig');
      const deliveryIDs = bulkUploadDeliveryIDsSelector(state$.value);
      return of(
        ...applyBestDiscountToOffer(deliveryIDs, offer, discountConfig),
      );
    }),
  );

const getBestDiscountWithOffers = (updateQuoteAndConfigs, offer) => {
  const { count, offerType } = offer;
  const deliveryCount = Object.entries(updateQuoteAndConfigs).length;
  if (!isRewardOffer(offerType) || count >= deliveryCount) {
    return { ...updateQuoteAndConfigs };
  }
  const bestDeliveries = [];
  Object.entries(updateQuoteAndConfigs).forEach(([deliveryID, delivery]) => {
    bestDeliveries.push({
      deliveryID,
      discountAmount:
        _get(delivery, ['quote', 'originalFare', 'upperBound'], 0) -
        _get(delivery, ['quote', 'finalFare', 'upperBound'], 0),
    });
  });

  // Sort descending to get the top discounts
  bestDeliveries.sort((a, b) => b.discountAmount - a.discountAmount);
  const bestResults = {};
  for (let i = 0; i < count; i++) {
    const delivery = bestDeliveries[i];
    const updateQuoteAndConfig = updateQuoteAndConfigs[delivery.deliveryID];
    if (updateQuoteAndConfig) {
      bestResults[delivery.deliveryID] = updateQuoteAndConfig;
    }
  }

  return bestResults;
};

const applyBestDiscountToOffer = (deliveryIDs, offer, discountConfig) => {
  const obs = [];
  deliveryIDs.forEach(deliveryID => {
    const discountInfo = discountConfig[deliveryID];
    if (discountInfo) {
      const newOffer = {
        ...offer,
        offerType: _get(discountInfo, ['quote', 'discount', 'type']),
      };
      obs.push(NewBookingDuck.creators.setOfferConfig(deliveryID, newOffer));
    } else {
      obs.push(NewBookingDuck.creators.setOfferConfig(deliveryID, null));
    }
  });
  obs.push(
    NewBookingDuck.creators.batchUpdateDeliveryQuotesAndConfigs({
      ...discountConfig,
    }),
  );
  obs.push(NewBulkUploadDuck.creators.setOfferConfig(offer));
  obs.push(NewBulkUploadDuck.creators.setApplicableOfferConfig(null));

  return obs;
};

const groupingTriggerClusteringDeliveriesEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBulkUploadDuck.types.GROUPING_TRIGGER_CLUSTERING_DELIVERIES),
    mergeMap(action => {
      const { payload } = action;
      const obs = [];
      payload.forEach(deliveryIDs => {
        const deliveries = deliveriesFromPropsSelector(state$.value, {
          deliveryIDs,
        });
        const pickupPOI = _get(deliveries[0], ['detail', 'pickUp']);
        const pickup = {
          id: _get(pickupPOI, ['poi', 'id']),
          cityID: _get(pickupPOI, ['poi', 'address', 'cityId']),
          coordinate: _get(pickupPOI, ['poi', 'latlng']),
          senderName: _get(pickupPOI, ['name']),
          phoneNumber: _get(pickupPOI, ['phone']),
        };
        const dropoffs = deliveries.map(delivery => {
          const dropoff = _get(delivery, ['detail', 'dropOffs', '0']);
          const { categoryID } = parseItemInfo(_get(dropoff, ['itemType']));
          return {
            id: _get(dropoff, ['poi', 'id']),
            coordinate: _get(dropoff, ['poi', 'latlng']),
            weight: _get(dropoff, ['itemWeight']),
            itemCategoryID: categoryID,
            recipientName: _get(dropoff, ['name']),
            phoneNumber: _get(dropoff, ['phone']),
          };
        });

        const vehicleType = _get(
          deliveries[0],
          ['detail', 'configuration', 'vehicleType'],
          null,
        );
        const specialServiceType = _get(
          deliveries[0],
          ['detail', 'configuration', 'specialServiceType'],
          null,
        );
        const additionalServiceTypes = _get(
          deliveries[0],
          ['detail', 'configuration', 'additionalServices'],
          [],
        ).map(additionalService => additionalService.id);
        const isPax = isPaxSelector(state$.value);
        obs.push(
          fetchObservableUtil(
            getClusteringEnpoint(),
            {
              method: 'POST',
              headers: buildBulkUploadFetchHeader(state$),
              credentials: buildCredentials(isPax),
              body: JSON.stringify({
                pickup,
                dropoffs,
                vehicleType,
                specialServiceType,
                additionalServiceTypes,
              }),
            },
            { rethrow: true },
          ),
        );
      });

      return forkJoin([of(payload), ...obs]).pipe(
        mergeMap(([firstAttemptClusters, ...responses]) => {
          const deliveryClusters = [];
          const proceeddDeliveryIDs = [];
          responses.forEach((response, index) => {
            const { clusters } = response;
            const deliveries = deliveriesFromPropsSelector(state$.value, {
              deliveryIDs: firstAttemptClusters[index],
            });

            // Flatten the responses
            clusters
              .filter(
                cluster =>
                  _get(cluster, 'dropoffs', []) && cluster.dropoffs.length > 1,
              )
              .forEach(cluster => {
                const clusteredDeliveryIDs = cluster.dropoffs.map(
                  clusteredDropOff => {
                    const dropOffDelivery = deliveries.find(
                      delivery =>
                        _get(delivery, [
                          'detail',
                          'dropOffs',
                          '0',
                          'poi',
                          'id',
                        ]) === clusteredDropOff.id &&
                        !proceeddDeliveryIDs.includes(_get(delivery, 'id')),
                    );
                    const dropOffDeliveryID = _get(dropOffDelivery, 'id', null);

                    // To ensure if there is 2 or more deliveries has same dropOffs
                    proceeddDeliveryIDs.push(dropOffDeliveryID);
                    return dropOffDeliveryID;
                  },
                );
                deliveryClusters.push({
                  recommendedTaxitypeID: _get(cluster, [
                    'recommendedTaxitypeID',
                  ]),
                  recommendedVehicleType: _get(cluster, [
                    'recommendedVehicleType',
                  ]),
                  clusteredDeliveryIDs: clusteredDeliveryIDs.filter(
                    deliveryID => !!deliveryID,
                  ),
                });
              });
          });
          const obs = [];
          const allDeliveries = bulkUploadDeliveriesSelector(state$.value);

          if (
            _every(deliveryClusters, cluster =>
              _isEmpty(Object.values(cluster)),
            )
          ) {
            return of(
              NewBulkUploadDuck.creators.groupingSetTrialDeliveries(null, null),
              NewBulkUploadDuck.creators.groupingSetFetchingQuote(false),
            );
          }

          const newMSDDeliveries = {};
          const clusterMappings = [];
          deliveryClusters.forEach(
            ({ recommendedVehicleType, clusteredDeliveryIDs }) => {
              const newMSDDelivery = mergeDeliveriesIntoMSD(
                clusteredDeliveryIDs,
                allDeliveries,
                deliveryIDPrefixes.BulkUpload,
                recommendedVehicleType
                  ? {
                      vehicleType: recommendedVehicleType,
                    }
                  : undefined,
              );
              newMSDDeliveries[newMSDDelivery.id] = newMSDDelivery;
              clusterMappings.push({
                groupDeliveryID: newMSDDelivery.id,
                originalDeliveryIDs: clusteredDeliveryIDs,
              });
            },
          );

          obs.push(
            NewBulkUploadDuck.creators.groupingSetTrialDeliveries(
              newMSDDeliveries,
              clusterMappings,
            ),
          );
          obs.push(NewBulkUploadDuck.creators.groupingFetchTrialQuote());
          return of(...obs);
        }),
        catchError(() =>
          of(
            NewBulkUploadDuck.creators.groupingSetTrialDeliveries(null, null),
            NewBulkUploadDuck.creators.groupingSetFetchingQuote(false),
          ),
        ),
      );
    }),
  );

const groupingFetchTrialQuoteEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBulkUploadDuck.types.GROUPING_FETCH_QUOTE_TRIAL_GROUPING),
    mergeMap(action => {
      const trialDeliveries = bulkUploadGroupingTrialDeliveriesSelector(
        state$.value,
      );
      const deliveriesToGetQuote = Object.values(trialDeliveries);

      return forkJoin(
        of(deliveriesToGetQuote),
        ajaxFetchDeliveriesQuote(deliveriesToGetQuote, state$.value),
      );
    }),
    mergeMap(([deliveriesToGetQuote, resp]) => {
      // filter deliveries has RO after quote
      const { response, error } = resp;
      const payloads = _get(response, 'quotes', []);

      if (error || payloads.length !== deliveriesToGetQuote.length) {
        return of(NewBulkUploadDuck.creators.groupingCompleteResetState());
      }

      // Update Route optimization & map back to deliveries
      const updatedDeliveries = {};
      deliveriesToGetQuote.forEach((delivery, i) => {
        const payload = payloads[i];
        const deliveryID = _get(delivery, 'id');
        const routeOptimizationSignature = _get(
          payload,
          'quote.alternativeFare[0].signature',
        );

        // Error handler: Skip if there is paxExpressError
        if (_get(payload, ['paxExpressError'])) {
          return;
        }

        // Update routeOptimization
        if (routeOptimizationSignature) {
          const routeOptimizationResult = _get(
            payload,
            'quote.alternativeFare[0].routeOptimization',
          );
          if (routeOptimizationResult) {
            const [isROApplied, newDeliveryDetail] = applyRouteOptimization(
              delivery,
              routeOptimizationResult,
            );
            if (isROApplied) {
              delivery.detail.dropOffs = _get(newDeliveryDetail, [
                'detail',
                'dropOffs',
              ]);
            }
          }
        }

        updatedDeliveries[deliveryID] = {
          ...delivery,
          quote: undefined,
          fareDetailMatrix: undefined,
          config: undefined,
          paxExpressError: undefined,
          ...payload,
        };
      });

      // Filter again which deliveries cannot get quotes

      const clusterMappings = bulkUploadGroupingClusterMappingsSelector(
        state$.value,
      );
      const newClusterMappings = [];

      clusterMappings.forEach(mapping => {
        if (updatedDeliveries[mapping.groupDeliveryID]) {
          newClusterMappings.push(_cloneDeep(mapping));
        }
      });

      return of(
        NewBulkUploadDuck.creators.groupingSetTrialDeliveries(
          updatedDeliveries,
          newClusterMappings,
        ),
        NewBulkUploadDuck.creators.groupingSetFetchingQuote(false),
      );
    }),
  );

const groupingSwapDeliveriesEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBulkUploadDuck.types.GROUPING_SWAP_DELIVERIES),
    mergeMap(action => {
      const { isGrouped } = action;
      const obs = [];

      const clusterMappings = bulkUploadGroupingClusterMappingsSelector(
        state$.value,
      );

      const addDeliveryIDs = [],
        removeDeliveryIDs = [];

      if (isGrouped) {
        const trialDeliveries = bulkUploadGroupingTrialDeliveriesSelector(
          state$.value,
        );

        obs.push(
          NewBookingDuck.creators.addDeliveryDetails(
            Object.values(trialDeliveries),
          ),
        );

        clusterMappings.forEach(mapping => {
          const { groupDeliveryID, originalDeliveryIDs } = mapping;
          addDeliveryIDs.push(groupDeliveryID);
          removeDeliveryIDs.push(...originalDeliveryIDs);
        });

        if (!_isEmpty(addDeliveryIDs)) {
          obs.push(
            pushNotificationAction({
              id: `ungroup-deliveries ${uuidV4()}`,
              type: NOTIFICATION_TYPE.CONFIRMED,
              message: I18n.t(
                'bulkUploadRevamp.step3.grouping.notifications.group.message',
                { amt: removeDeliveryIDs.length },
              ),
              description: I18n.t(
                'bulkUploadRevamp.step3.grouping.notifications.group.decription',
              ),
            }),
          );
        }
      } else {
        clusterMappings.forEach(mapping => {
          const { groupDeliveryID, originalDeliveryIDs } = mapping;
          removeDeliveryIDs.push(groupDeliveryID);
          addDeliveryIDs.push(...originalDeliveryIDs);
        });

        if (!_isEmpty(removeDeliveryIDs)) {
          obs.push(
            NewBookingDuck.creators.removeDeliveryDetails(removeDeliveryIDs),
          );
          obs.push(
            pushNotificationAction({
              id: `ungroup-deliveries ${uuidV4()}`,
              type: NOTIFICATION_TYPE.CONFIRMED,
              message: I18n.t(
                'bulkUploadRevamp.step3.grouping.notifications.ungroup.message',
              ),
              description: I18n.t(
                'bulkUploadRevamp.step3.grouping.notifications.ungroup.decription',
                { amt: removeDeliveryIDs.length },
              ),
            }),
          );
        }
      }

      obs.push(NewBulkUploadDuck.creators.addDeliveryIDs(addDeliveryIDs));
      obs.push(NewBulkUploadDuck.creators.removeDeliveryIDs(removeDeliveryIDs));
      return of(...obs);
    }),
  );

const groupingResetStateEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBulkUploadDuck.types.GROUPING_RESET_STATE),
    mergeMap(action => {
      const obs = [];
      const clusterMappings = bulkUploadGroupingClusterMappingsSelector(
        state$.value,
      );

      if (clusterMappings) {
        const groupedDeliveryIDs = clusterMappings.map(mapping =>
          _get(mapping, 'groupDeliveryID'),
        );
        obs.push(
          NewBookingDuck.creators.removeDeliveryDetails(groupedDeliveryIDs),
        );
      }

      obs.push(NewBulkUploadDuck.creators.groupingCompleteResetState());

      return of(...obs);
    }),
  );

const splitDropOffEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBulkUploadDuck.types.SPLIT_DROPOFF),
    mergeMap(action => {
      const { deliveryID, index } = action;
      const obs = [];
      const newDeliveryID = genDeliveryID(deliveryIDPrefixes.BulkUpload);

      const oldDelivery = deliverySelector(state$.value, { deliveryID });
      const dropOffs = _get(oldDelivery, ['detail', 'dropOffs'], []);
      const splitedDropOff = _cloneDeep(dropOffs[index]);
      const newDropOffs = _cloneDeep(dropOffs);
      newDropOffs.splice(index, 1);

      obs.push(
        NewBookingDuck.creators.setDropOffs(deliveryID, newDropOffs, true),
      );
      // Copy the whole delivery id with new stop
      obs.push(
        NewBookingDuck.creators.copyDelivery(deliveryID, newDeliveryID, [
          splitedDropOff,
        ]),
      );
      // Add new delivery to Bulk deliveryIDs list
      obs.push(NewBulkUploadDuck.creators.addDeliveryIDs([newDeliveryID]));

      const clusterMappings = bulkUploadGroupingClusterMappingsSelector(
        state$.value,
      );

      const mapping = clusterMappings.find(
        mapping => mapping.groupDeliveryID === deliveryID,
      );
      if (mapping) {
        const groupedDeliveries = deliveriesFromPropsSelector(state$.value, {
          deliveryIDs: mapping.originalDeliveryIDs,
        });
        const splitedDropOffPoiID = _get(splitedDropOff, ['poi', 'id']);

        const splitedDelivery = groupedDeliveries.find(
          delivery =>
            _get(delivery, ['detail', 'dropOffs', '0', 'poi', 'id']) ===
            splitedDropOffPoiID,
        );
        mapping.originalDeliveryIDs = mapping.originalDeliveryIDs.filter(
          deliveryID => deliveryID !== splitedDelivery.id,
        );

        obs.push(
          NewBulkUploadDuck.creators.groupingSetTrialDeliveries(
            bulkUploadGroupingTrialDeliveriesSelector(state$.value),
            _cloneDeep(clusterMappings),
          ),
        );
      }

      return of(...obs);
    }),
  );

export default combineEpics(
  deleteDeliveryEpic,
  createNewDeliveriesEpic,
  fetchPOIsForRawDataEpic,
  fetchMetaDataEpic,
  fetchPromoEpic,
  inputPromoCodeEpic,
  trySetOfferConfigBulkEpic,
  trySetOfferConfigBulkNullEpic,
  applyApplicableOfferConfigEpic,
  groupingTriggerClusteringDeliveriesEpic,
  groupingFetchTrialQuoteEpic,
  groupingSwapDeliveriesEpic,
  groupingResetStateEpic,
  splitDropOffEpic,
);
